import axiosInstance from './AxiosInstance';

const DepositsService = {
  // 1. Create Deposit
  createDeposit: async (depositData) => {
    try {
      const response = await axiosInstance.post('/deposits', depositData);
      return { success: response.data.success, data: response.data.data, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create deposit',
        errors: error.response?.data?.errors || []
      };
    }
  },

  // 2. Get All Deposits
  getAllDeposits: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.companyId) queryParams.append('companyId', params.companyId);
      if (params.branchId) queryParams.append('branchId', params.branchId);
      if (params.depositorId) queryParams.append('depositorId', params.depositorId);
      if (params.status) queryParams.append('status', params.status);
      if (params.search) queryParams.append('search', params.search);
      if (params.fromDate) queryParams.append('fromDate', params.fromDate);
      if (params.toDate) queryParams.append('toDate', params.toDate);
      if (params.partnerId) queryParams.append('partnerId', params.partnerId);
      
      const response = await axiosInstance.get(`/deposits?${queryParams.toString()}`);
      return {
        success: response.data.success,
        data: response.data.data.data,
        pagination: response.data.data.pagination,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch deposits',
        data: [],
        pagination: {}
      };
    }
  },

  // 3. Get Deposit by ID
  getDepositById: async (id) => {
    try {
      const response = await axiosInstance.get(`/deposits/${id}`);
      return { success: response.data.success, data: response.data.data, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch deposit',
      };
    }
  },

  // 4. Update Deposit
  updateDeposit: async (id, updateData) => {
    try {
      const response = await axiosInstance.put(`/deposits/${id}`, updateData);
      return { success: response.data.success, data: response.data.data, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update deposit',
        errors: error.response?.data?.errors || []
      };
    }
  },

  // 5. Close Deposit
  closeDeposit: async (id) => {
    try {
      const response = await axiosInstance.put(`/deposits/${id}/close`);
      return { success: response.data.success, data: response.data.data, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to close deposit',
      };
    }
  },

  // 6. Get Deposit Transaction History
  getDepositHistory: async (id, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.transactionType) queryParams.append('transactionType', params.transactionType);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      const response = await axiosInstance.get(`/deposits/${id}/history?${queryParams.toString()}`);
      return {
        success: response.data.success,
        data: response.data.data.data,
        pagination: response.data.data.pagination,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch deposit history',
        data: [],
        pagination: {}
      };
    }
  },

  // 7. Get Deposits Dropdown
  getDepositsDropdown: async (branchId) => {
    try {
      const response = await axiosInstance.get(`/deposits/dropdown?branchId=${branchId}`);
      return { success: response.data.success, data: response.data.data, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch deposits dropdown',
        data: []
      };
    }
  },

  // 8. Get Depositor Deposit Summary
  getDepositorDepositSummary: async () => {
    try {
      const response = await axiosInstance.get(`/deposits/depositor/summary-scoped`);
      return { success: response.data.success, data: response.data.data, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch deposit summary',
      };
    }
  },

  // 9. Create Deposit Transaction (CREDIT/DEBIT)
  createDepositTransaction: async (depositId, transactionData) => {
    try {
      const response = await axiosInstance.post(`/deposits/${depositId}/history`, transactionData);
      return { 
        success: response.data.success, 
        data: response.data.data, 
        message: response.data.message 
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create transaction',
        errors: error.response?.data?.errors || []
      };
    }
  },

  // 10. Get Deposit Interest History
  getDepositInterestHistory: async (id, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      const response = await axiosInstance.get(`/deposits/${id}/interest-history?${queryParams.toString()}`);
      return {
        success: response.data.success,
        data: response.data.data.data,
        pagination: response.data.data.pagination,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch interest history',
        data: [],
        pagination: {}
      };
    }
  },

  // 11. Update Interest Rate
  updateInterestRate: async (depositId, interestData) => {
    try {
      const response = await axiosInstance.post(`/deposits/${depositId}/interest-history`, interestData);
      return { 
        success: response.data.success, 
        data: response.data.data, 
        message: response.data.message 
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update interest rate',
        errors: error.response?.data?.errors || []
      };
    }
  },

  // Get deposits statistics
  getDepositsStatistics: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.companyId) queryParams.append('companyId', params.companyId);
      if (params.branchId) queryParams.append('branchId', params.branchId);

      const url = `/statistics/deposits${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await axiosInstance.get(url);
      
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch deposits statistics',
        data: {
          activeDeposits: 0,
          maturedDeposits: 0,
          totalDeposits: 0,
          totalAmount: 0
        }
      };
    }
  }
  ,
  // 18. Get Depositor Aggregates for a Partner
  getDepositorAggregatesByPartner: async (partnerId, params = {}) => {
    try {
      if (!partnerId) return { success: false, message: 'partnerId is required', data: { items: [], pagination: {} } };
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      // support common filter params: companyId, branchId, status, search, createdAt/updatedAt -> fromDate/toDate
      if (params.companyId) queryParams.append('companyId', params.companyId);
      if (params.branchId) queryParams.append('branchId', params.branchId);
      if (params.status) queryParams.append('status', params.status);
      if (params.search) queryParams.append('search', params.search);
      const fromDate = params.fromDate || params.createdAt || params.startDate || params.startDate;
      const toDate = params.toDate || params.updatedAt || params.endDate || params.endDate;
      if (fromDate) queryParams.append('fromDate', fromDate);
      if (toDate) queryParams.append('toDate', toDate);

      const url = `/deposits/partner/${partnerId}/depositor-aggregates${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await axiosInstance.get(url);
      // expecting response.data.data to contain { items: [], pagination: {} }
      return {
        success: response.data.success,
        data: response.data.data?.items || [],
        pagination: response.data.data?.pagination || {},
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch depositor aggregates',
        data: [],
        pagination: {},
      };
    }
  },
  // Get partners with depositor aggregates (paginated)
  getPartnersDepositorAggregates: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.companyId) queryParams.append('companyId', params.companyId);
      if (params.branchId) queryParams.append('branchId', params.branchId);
      if (params.status) queryParams.append('status', params.status);
      if (params.search) queryParams.append('search', params.search);
      const fromDate = params.fromDate || params.createdAt || params.startDate;
      const toDate = params.toDate || params.updatedAt || params.endDate;
      if (fromDate) queryParams.append('fromDate', fromDate);
      if (toDate) queryParams.append('toDate', toDate);

      const url = `/deposits/partners/depositor-aggregates${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await axiosInstance.get(url);
      // expecting response.data.data to contain { items: [], pagination: {} }
      return {
        success: response.data.success,
        data: response.data.data?.items || [],
        pagination: response.data.data?.pagination || {},
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch partners depositor aggregates',
        data: [],
        pagination: {},
      };
    }
  },
  // 12. Get Deposits History (global across deposits)
  getDepositsHistory: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.companyId) queryParams.append('companyId', params.companyId);
      if (params.branchId) queryParams.append('branchId', params.branchId);
      if (params.transactionType) queryParams.append('transactionType', params.transactionType);
      if (params.search) queryParams.append('search', params.search);
      if (params.fromDate) queryParams.append('fromDate', params.fromDate);
      if (params.toDate) queryParams.append('toDate', params.toDate);
      if (params.startDate) queryParams.append('fromDate', params.startDate);
      if (params.endDate) queryParams.append('toDate', params.endDate);

      const url = `/deposits/deposits-history${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await axiosInstance.get(url);
      return {
        success: response.data.success,
        data: response.data.data,
        pagination: response.data.pagination,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch deposits history',
        data: [],
        pagination: {}
      };
    }
  }
  ,
  // 17. Download deposits history as XLSX
  downloadDepositsHistory: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.companyId) queryParams.append('companyId', params.companyId);
      if (params.branchId) queryParams.append('branchId', params.branchId);
      if (params.transactionType) queryParams.append('transactionType', params.transactionType);
      if (params.search) queryParams.append('search', params.search);
      if (params.fromDate) queryParams.append('fromDate', params.fromDate);
      if (params.toDate) queryParams.append('toDate', params.toDate);

      const url = `/deposits/deposits-history/download/xlsx${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await axiosInstance.get(url, {
        responseType: 'blob',
        headers: {
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      });
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  // 16. Download deposits as XLSX
  downloadDeposits: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.companyId) queryParams.append('companyId', params.companyId);
      if (params.branchId) queryParams.append('branchId', params.branchId);
      if (params.depositorId) queryParams.append('depositorId', params.depositorId);
      if (params.status) queryParams.append('status', params.status);
      if (params.search) queryParams.append('search', params.search);
      if (params.fromDate) queryParams.append('fromDate', params.fromDate);
      if (params.toDate) queryParams.append('toDate', params.toDate);

      const url = `/deposits/download/xlsx${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await axiosInstance.get(url, {
        responseType: 'blob',
        headers: {
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      });
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  // 14. Get Transaction History (server can filter historyType=Interest)
  getTransactionHistory: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.companyId) queryParams.append('companyId', params.companyId);
      if (params.branchId) queryParams.append('branchId', params.branchId);
      if (params.transactionType) queryParams.append('transactionType', params.transactionType);
      if (params.search) queryParams.append('search', params.search);
      if (params.fromDate) queryParams.append('fromDate', params.fromDate);
      if (params.toDate) queryParams.append('toDate', params.toDate);

      const url = `/transaction-history/transaction-history${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await axiosInstance.get(url);

      // API may return { data: { data: [...], pagination: {...} } }
      const respData = response.data?.data;
      if (respData && respData.data !== undefined && respData.pagination !== undefined) {
        return {
          success: response.data.success,
          data: respData.data,
          pagination: respData.pagination,
          message: response.data.message,
        };
      }

      return {
        success: response.data.success,
        data: response.data.data || [],
        pagination: response.data.pagination || {},
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch transaction history',
        data: [],
        pagination: {},
      };
    }
  },
  // 13. Get Deposits History Aggregate (day/week/month)
  getDepositsHistoryAggregate: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.view) queryParams.append('view', params.view);
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.companyId) queryParams.append('companyId', params.companyId);
      if (params.branchId) queryParams.append('branchId', params.branchId);
      if (params.fromDate) queryParams.append('fromDate', params.fromDate);
      if (params.toDate) queryParams.append('toDate', params.toDate);
      if (params.startDate) queryParams.append('fromDate', params.startDate);
      if (params.endDate) queryParams.append('toDate', params.endDate);

      const url = `/deposits/deposits-history/aggregate${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await axiosInstance.get(url);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch deposits history aggregate',
        data: []
      };
    }
  }
  ,
  // 15. Get Transaction History Aggregate (day/week/month) - for Interest etc.
  getTransactionHistoryAggregate: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.view) queryParams.append('view', params.view);
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.companyId) queryParams.append('companyId', params.companyId);
      if (params.branchId) queryParams.append('branchId', params.branchId);
      if (params.fromDate) queryParams.append('fromDate', params.fromDate);
      if (params.toDate) queryParams.append('toDate', params.toDate);
      if (params.startDate) queryParams.append('fromDate', params.startDate);
      if (params.endDate) queryParams.append('toDate', params.endDate);

      const url = `/transaction-history/aggregate${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await axiosInstance.get(url);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch transaction history aggregate',
        data: []
      };
    }
  }
};

export default DepositsService;
